import type { ObjectId } from 'bson';
import type { Document, LeanDocument } from 'mongoose';
import type { AWSRegion } from '../aws';

export interface AmiStateChangeUpdate {
  amiId: string;
  region: AWSRegion;
  state: EC2ImageState;
}

export enum EC2ImageState {
  AVAILABLE = 'available',
  PENDING = 'pending',
  FAILED = 'failed',
  DELETED = 'deleted',
  DEREGISTERED = 'deregistered',
  UNAVAILABLE = 'unavailable',
  IMPORTING = 'importing',
  IMPORT_FAILED = 'import-failed',
  MIGRATING = 'migrating',
  UNKNOWN = 'unknown',
}

export enum UploadType {
  VM = 'vm',
  FILE = 'file',
  THM = 'THM',
  KOTH = 'KOTH',
  STATIC_SITE = 'static-site',
  INSTALLABLE = 'installable',
  DOWNLOADABLE = 'downloadable',
  UPLOADABLE = 'uploadable',
  AZURE = 'azure',
}

export enum UploadLocation {
  S3_BUCKET = 'S3_Bucket',
  AZURE = 'Azure',
}

export enum InstanceLaunchType {
  SPOT = 'spot',
  ON_DEMAND = 'on-demand',
}

export enum UploadRemoteConnType {
  GUAC = 'guac',
  NO_VNC = 'novnc',
  WEB_PROXY = 'web-proxy',
}

export enum ProtocolType {
  VNC = 'vnc',
  SSH = 'ssh',
  RDP = 'rdp',
  HTTP = 'http',
  HTTPS = 'https',
}

export enum ReadyToDeploy {
  YES = 'Yes',
  NO = 'No',
}

export enum InstanceType {
  EMPTY = '',

  // T2 Family
  T2NANO = 't2.nano',
  T2MICRO = 't2.micro',
  T2SMALL = 't2.small',
  T2MEDIUM = 't2.medium',
  T2LARGE = 't2.large',

  // T3 Family
  T3NANO = 't3.nano',
  T3MICRO = 't3.micro',
  T3SMALL = 't3.small',
  T3MEDIUM = 't3.medium',
  T3LARGE = 't3.large',
  T3XLARGE = 't3.xlarge',

  // T3a Family
  T3ANANO = 't3a.nano',
  T3AMICRO = 't3a.micro',
  T3ASMALL = 't3a.small',
  T3AMEDIUM = 't3a.medium',
  T3ALARGE = 't3a.large',
}

export enum OperatingSystemType {
  LINUX = 'linux',
  WINDOWS = 'windows',
}

export enum UploadInfo {
  SUCCESS = 'Successfully converted.',
  PROBLEM_OCCURRED = 'Problem converting VM. <a href="/upload">Check prerequisites.</a>',
}

export interface EC2ImageResponse {
  id: string;
  name: string;
  creationDate: Date;
  state: string;
  sourceImageId: string;
  images: Record<string, { imageId: string; state: EC2ImageState }>;
}

export interface CopyImageToTargetRegionRequestParams {
  name: string;
  sourceImageId: string;
  sourceRegion?: string;
  targetRegion: string;
  action: 'copy' | 'migrate';
}

export interface AwsConfigDocument {
  region: string;
  subnet: string;
  sg: string;
}

export interface RemoteConfigDocument {
  active: boolean;
  username: string;
  password: string;
  port: number;
  maxConns: number;
  protocol: string;
  connType: UploadRemoteConnType;
  bootTime: number;
}
export interface FlagDocument {
  value: string;
  points: number;
}

export interface StaticSiteDocument {
  title: string;
  url: string;
  autoOpen: boolean;
}

export interface CredentialsDocument {
  username: string;
  password: string;
  home: string;
  enabled: boolean;
  accessMethod?: string;
  port?: number;
}

export interface AzureLabActionDocument {
  _id?: ObjectId;
  label: string;
  azureFunctionUrl?: string;
  executionResponse?: Record<string, string>;
}

export interface AzureConfigDocument {
  lab: string;
  roleDefinitionId?: string;
  isMultiTenantLab: boolean;
  joinUrl: string;
  openUrl?: string;
  leaveUrl?: string;
  actions: AzureLabActionDocument[];
}

export interface ImageDocument {
  imageId: string;
  state: EC2ImageState;
}

export interface UsageTracking {
  tasks: ObjectId[];
  socSim: ObjectId[];
  threatHunting: ObjectId[];
  networksV2: ObjectId[];
  lastUpdated: Date;
}

/**
 * @description Intentionally smaller interface, used to return key upload document information.
 */
export interface SlimUpload {
  id: string;
  title: string;
  description: string;
  location: string;
  images?: Record<AWSRegion, ImageDocument>;
}

export interface ImageMigration {
  _id: ObjectId;
  sourceRegion: AWSRegion;
  sourceImageId: string;
  targetRegion: AWSRegion;
  targetImageId: string;
  startedBy: ObjectId;
  startTime: Date;
  endTime?: Date;
  state: 'in-progress' | 'completed' | 'failed';
}
export interface Upload {
  userId: string;
  title: string;
  file_name: string;
  description: string;
  type: UploadType;
  location: string;
  readyToDeploy: ReadyToDeploy;
  deleted: boolean;
  access: string[];
  vmDeploymentStatus: number;
  created: Date;
  instanceLaunchType: InstanceLaunchType;
  instanceType: InstanceType;
  info: string;
  os: OperatingSystemType;
  aws: AwsConfigDocument;
  remote: RemoteConfigDocument;
  flags: Array<FlagDocument>;
  static: Array<StaticSiteDocument>;
  credentials: CredentialsDocument;
  azure?: AzureConfigDocument;
  isBadrEnabled?: boolean;
  protectTermination?: boolean;
  scheduledDeletionDate?: Date;
  images?: Record<AWSRegion, ImageDocument>;
  imageMigrations?: ImageMigration[];
  usageTracking?: UsageTracking;
}

export type UploadDocument = Document & Upload;

export type LeanUploadDocument = LeanDocument<
  Upload & {
    _id: ObjectId;
  }
>;

export interface PromoteAMIsRequestParams {
  defaultImage: string;
  regionalImages: Record<string, { imageId: string; state: EC2ImageState }>;
}
